import { supabase } from "@/integrations/supabase/client";
import { logger } from "@/lib/logger";

export const cleanupAuthState = () => {
  // Remove standard auth tokens
  localStorage.removeItem("supabase.auth.token");

  // Remove all Supabase auth keys from localStorage
  Object.keys(localStorage).forEach((key) => {
    if (key.startsWith("supabase.auth.") || key.includes("sb-")) {
      localStorage.removeItem(key);
    }
  });

  // Remove from sessionStorage if in use
  Object.keys(sessionStorage || {}).forEach((key) => {
    if (key.startsWith("supabase.auth.") || key.includes("sb-")) {
      sessionStorage.removeItem(key);
    }
  });
};

export const performRobustSignOut = async () => {
  try {
    logger.info("Starting robust sign out process...");

    // Step 1: Clean up auth state immediately to ensure UI updates
    cleanupAuthState();
    logger.info("Auth state cleaned up");

    // Step 2: Try multiple sign-out approaches
    const signOutAttempts = [
      // First try: Global sign out
      async () => {
        logger.info("Attempting global sign out...");
        await supabase.auth.signOut({ scope: "global" });
      },
      // Second try: Local sign out only
      async () => {
        logger.info("Attempting local sign out...");
        await supabase.auth.signOut({ scope: "local" });
      },
      // Third try: Direct session invalidation
      async () => {
        logger.info("Attempting direct session invalidation...");
        await supabase.auth.signOut();
      },
    ];

    // Try each approach, continuing if one fails
    for (let i = 0; i < signOutAttempts.length; i++) {
      try {
        await signOutAttempts[i]();
        logger.info(`Sign out approach ${i + 1} succeeded`);
        break; // If successful, exit the loop
      } catch (error) {
        logger.warn(`Sign out approach ${i + 1} failed`, { error });
        // Continue to next approach
        if (i === signOutAttempts.length - 1) {
          logger.info("All sign out approaches failed, but auth state is cleaned up");
        }
      }
    }

    // Step 3: Additional cleanup - clear any remaining session data
    try {
      // Force clear any cached session
      const { error } = await supabase.auth.getSession();
      if (error) {
        logger.info("Session already cleared or invalid");
      }
    } catch (_err) {
      logger.info("Session check failed (expected after sign out)");
    }

    logger.info("Robust sign out completed");
  } catch (error) {
    logger.error("Error during robust sign out:", { error: error });
    // Even if everything fails, we've cleaned up the local state
  }
};

/**
 * Recover an expired session by attempting to refresh it
 * @returns Promise<boolean> - true if session was recovered, false otherwise
 */
export const recoverSession = async (): Promise<boolean> => {
  try {
    logger.info("🔄 Attempting session recovery...");

    // First, check if we have a current session
    const {
      data: { session },
      error: sessionError,
    } = await supabase.auth.getSession();

    if (sessionError) {
      const errorMessage =
        sessionError instanceof Error
          ? sessionError.message
          : String(sessionError);
      logger.warn("⚠️ Session check failed", { error: errorMessage });
    }

    // If we have a session, check if it's expired
    if (session) {
      const expiresAt = session.expires_at
        ? new Date(session.expires_at * 1000)
        : undefined;
      const now = new Date();

      if (expiresAt && expiresAt > now) {
        logger.info("✅ Current session is still valid");
        return true;
      }
    }

    // Try to refresh the session
    const { data, error } = await supabase.auth.refreshSession();

    if (error) {
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      logger.warn("⚠️ Session refresh failed", { error: errorMessage });
      // Do NOT sign out here — signing out during recovery creates a death spiral
      // where a transient failure (network, timing) permanently logs the user out.
      return false;
    }

    if (data.session) {
      logger.info("✅ Session recovered successfully");
      return true;
    }

    return false;
  } catch (error) {
    logger.error("❌ Session recovery failed:", { error: error });
    return false;
  }
};

/**
 * Check if an error is authentication-related
 */
export const isAuthError = (error: unknown): boolean => {
  if (!error) {return false;}

  const authIndicators = [
    "JWT",
    "unauthorized",
    "authentication",
    "token",
    "expired",
    "401",
    "403",
    "406", // Also check for 406 Not Acceptable which can indicate auth issues
    "PGRST301", // Supabase auth error code
    "PGRST116", // Row not found (could be RLS)
    "42501", // RLS policy violation
  ];

  const errObj = error as {
    message?: string;
    code?: string;
    status?: unknown;
  } | null;
  const message = errObj?.message ?? "";
  const code = errObj?.code ?? "";
  const errorString = `${message} ${code}`.toLowerCase();

  if (authIndicators.some((indicator) => errorString.includes(indicator.toLowerCase()))) {
    return true;
  }

  try {
    if (typeof error === 'object' && error !== null) {
      const safeObj = {
        message: errObj?.message,
        code: errObj?.code,
        status: errObj?.status,
      };
      const str = JSON.stringify(safeObj).toLowerCase();
      return authIndicators.some((indicator) => str.includes(indicator.toLowerCase()));
    }
  } catch (_e) {
    // ignore JSON.stringify failures on non-serializable errors
  }

  return false;
};

/**
 * Handle authentication errors with automatic recovery
 * @param error - The error to handle
 * @param context - Context string for logging
 * @returns Promise<boolean> - true if recovery was successful
 */
export const handleAuthError = async (
  error: unknown,
  context: string
): Promise<boolean> => {
  if (!isAuthError(error)) {
    return false;
  }

  logger.warn(`🔧 Auth error in ${context}`, { error });

  const recovered = await recoverSession();

  if (!recovered) {
    logger.warn("⚠️ Session recovery failed, user needs to sign in again");
  }

  return recovered;
};
